import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import BusinessSection from "./components/BusinessSection";
import BrandingSection from "./components/BrandingSection";
import ServiceZonesSection from "./components/ServiceZonesSection";
import TeamSection from "./components/TeamSection";
import NotificationsSection from "./components/NotificationsSection";

type SectionId =
  | "business"
  | "branding"
  | "zones"
  | "team"
  | "notifications";

const sections: { id: SectionId; label: string; icon: string; hint: string }[] = [
  {
    id: "business",
    label: "Business",
    icon: "ri-store-2-line",
    hint: "Company name, contact, hours",
  },
  {
    id: "branding",
    label: "Branding",
    icon: "ri-palette-line",
    hint: "Logo, colors, voice",
  },
  {
    id: "zones",
    label: "Service zones",
    icon: "ri-map-pin-line",
    hint: "Where you take bookings",
  },
  {
    id: "team",
    label: "Team",
    icon: "ri-team-line",
    hint: "Admins and permissions",
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: "ri-notification-3-line",
    hint: "Alerts and digests",
  },
];

export default function AdminSettingsPage() {
  const [active, setActive] = useState<SectionId>("business");
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (msg: string) => setToast(msg);

  return (
    <AdminLayout title="Settings" subtitle="Tune PureNest to how you run">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Side nav */}
        <nav className="bg-background-50 border border-background-200 rounded-xl p-2 h-fit lg:sticky lg:top-32">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer transition-colors ${
                active === s.id
                  ? "bg-primary-50 text-foreground-950"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <span
                className={`w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0 ${
                  active === s.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600"
                }`}
              >
                <i className={`${s.icon} text-base`}></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.label}</p>
                <p className="text-[10px] text-foreground-600 truncate">{s.hint}</p>
              </div>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="min-w-0">
          {active === "business" && <BusinessSection onSaved={showToast} />}
          {active === "branding" && <BrandingSection onSaved={showToast} />}
          {active === "zones" && <ServiceZonesSection onChange={showToast} />}
          {active === "team" && <TeamSection onChange={showToast} />}
          {active === "notifications" && <NotificationsSection onChange={showToast} />}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-foreground-950 text-background-50 text-xs px-4 py-2.5 rounded-full shadow animate-fade-up flex items-center gap-2">
          <i className="ri-check-line"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}